import React, { useContext, useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";

import SelectPolicyHolder from "../select-policy-holder/select-policy-holder";
import VariableData from "../variable-data/variable-data";
import Portal from "../../../../../components/portal/portal";
import ModalWithChildren from "../../../../../components/modal-with-children";
import Table from "../../../../../components/table-simple/table";
import Loader from "../../../../../components/loader";
import LeftInfoModal from "../../../../../components/left-info-modal";
import { Button } from "../../../../../components/buttons";
import { ModalContent } from "./personal-basic-data-styles";
import { PersonalInformationContainer } from "../../../../hirings/new-hiring/health/forms/personal-information/personal-information-style";
import { images } from "../../../../../assets";
import { InfoCard } from "../../../../hirings/new-hiring/health/health-questionnaire/health-questionnaire-data-style";

import SupplementsServices from "../../../../../services/SupplementsServices";
import { getTablePersonData, tableColumns } from "./helpers";
import { getPolicyNumber } from "../../utils";
import { ErrorDataModalProps, VariableDataProps } from "./personal-basic-data.types";
import { SupplementsContext } from "../../../../../context/supplements/SupplementsContext";

const PersonalBasicData: React.FC<VariableDataProps> = ({
  emailError,
  formData,
  formTitle,
  history,
  step,
  isClientChanges = false,
  handleMultipleChanges,
  setEmailError,
}) => {
  const { values, handleChange } = formData;
  const isMascotaPolicy = localStorage.getItem('policySubramo') === 'ASISA MASCOTAS';
  const supplementsContext = useContext(SupplementsContext);

  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [personTableData, setPersonTableData] = useState<{ [key: string]: string | number | boolean }[]>([]);
  const [errorDataModal, setErrorDataModal] = useState<ErrorDataModalProps>({
    showModal: false,
    error: false,
    msg: "",
  });

  useEffect(() => {
    if (showInfoModal) {
      getSeletedPersonData();
    }
  }, [showInfoModal]);

  const selectPerson = (name: string, value: any, step?: string) => {
    handleChange(name, value, step);
    if (isMascotaPolicy && value > 0) {
      return;
    }
    if (isClientChanges && name === "nameSelected" && value >= 0) {
      setIsLoading(true);
      setShowInfoModal(true);
    }
  };

  const getSeletedPersonData = async () => {
    const { docNumber } = values;
    const response = await SupplementsServices.getPersonData({
      setLoading: () => {},
      loader: false,
      docNum: docNumber,
      mediatorCode: values.mediator,
    });
    if (response.ok) {
      const newPersonTableData = getTablePersonData({ personData: response.data.customerPolicies, docNumber });
      const sendTo = response.data.sendTo;
      const isInsuredInSendaPolicy = response.data.isInsuredInSendaPolicy;
      supplementsContext.setSendTo(sendTo);
      supplementsContext.setIsInsuredInSendaPolicy(isInsuredInSendaPolicy);
      setPersonTableData(newPersonTableData);
      setErrorDataModal({ showModal: false, error: false, msg: "" });
    } else {
      setErrorDataModal({ showModal: true, error: true, msg: response.message });
      handleChange("nameSelected", "-1");
    }
    setIsLoading(false);
  };

  const goToSupplementsList = () => {
    history.push(`/suplementos/${getPolicyNumber()}`);
  };

  const modalContent = useMemo(() => {
    if (isLoading) return <></>;

    if (errorDataModal.showModal) {
      return (
        <LeftInfoModal
          img={images.WarningError}
          mainTitle="Error"
          mainText={errorDataModal.msg}
          noSecondaryButton={true}
          buttonText="Cerrar"
          active={errorDataModal.showModal}
          onClickAccept={() => setErrorDataModal((prev) => ({ ...prev, showModal: false }))}
          close={() => setErrorDataModal((prev) => ({ ...prev, showModal: false }))}
          className="error"
        />
      );
    }

    if (errorDataModal.error) {
      setShowInfoModal(false);
    }

    return (
      <ModalContent>
        <Table columns={tableColumns} data={personTableData} className="mini" />
        <div className="modal-content-info">
          <InfoCard origin={origin}>
            <div className="icon ">
              <img src={images.IconInfoBlue} alt="" />
            </div>
            <div className="content leftMargin10">
              <p>
                Los suplementos a las pólizas se realizarán a la Fecha de nueva situación, si no es
                la fecha deseada por favor cambie la fecha del Suplemento.
              </p>
            </div>
          </InfoCard>
        </div>
        <div className="modal-actions">
          <Button onClick={goToSupplementsList} text="Cancelar" variant="ghost" />
          <Button onClick={() => setShowInfoModal(false)} text="Continuar" />
        </div>
      </ModalContent>
    );
  }, [isLoading, errorDataModal, personTableData]);

  return (
    <PersonalInformationContainer>
      {isLoading && <Loader />}
      <div className="message margin-top-25">
        <p>Seleccione las opciones de modificación de datos que desea incluir en el suplemento </p>
      </div>
      <div className="content-wrapper">
        <div className="personal-data-wrapper">
          <div className="personal-data-fisica">
            <SelectPolicyHolder
              handleChange={selectPerson}
              handleMultipleChanges={handleMultipleChanges}
              noFormWidth
              isFirstTime={isFirstTime}
              setIsFirstTime={setIsFirstTime}
              values={values}
              title={"Interviniente en la póliza"}
              onlyInsureds={false}
            />
            <VariableData
              formData={formData}
              step={step}
              formTitle={formTitle}
              emailError={emailError}
              setEmailError={setEmailError}
            />
          </div>
        </div>
      </div>

      {isClientChanges && values.nameSelected !== "-1" && (
        <label className="check-label">
          <input
            type="checkbox"
            checked={values.checkPersonId}
            onChange={(e) => handleChange("checkPersonId", e.target.checked)}
            className={values.checkPersonId ? "checkbox-checked" : "checkbox-unchecked"}
          />
          <span>
            He revisado el documento identificativo del cliente y declaro que los datos introducidos
            son correctos
          </span>
        </label>
      )}

      <Portal>
        <ModalWithChildren
          active={showInfoModal && !isLoading}
          close={() => setShowInfoModal(false)}
          width="900px"
          maxWidth="80%"
        >
          {modalContent}
        </ModalWithChildren>
      </Portal>
    </PersonalInformationContainer>
  );
};

export default withRouter(PersonalBasicData);
